/**
 * Data transformation utilities for Risk Calculator
 * Shared between RiskCalculator.jsx and ResultsScreen.jsx
 */

/**
 * SP-442 row 13 — the culture name must be displayed in the language selected on
 * the Cultures search step. CultureTreeSelect persists that choice under this key.
 */
const CULTURE_LANG_STORAGE_KEY = 'lang_calculator_culture';

function getSelectedCultureLanguage() {
  try {
    return window.localStorage.getItem(CULTURE_LANG_STORAGE_KEY) || 'fr';
  } catch {
    return 'fr';
  }
}

/**
 * Resolve the culture display name in the selected language, falling back through
 * the other names so we never show a raw id.
 */
function resolveCultureName(info, cultureId) {
  const byLang = {
    fr: info.french_name,
    en: info.english_name,
    la: info.latin_name,
  }[getSelectedCultureLanguage()];
  return (
    byLang || info.title || info.label || info.french_name || info.name || cultureId
  );
}

/**
 * SP-442 row 28 — the dose column must NOT show the « Culture ou précision »
 * text. Units.display_name is `"{name} ({culture_or_precision})"`, and the unit
 * dropdown intentionally stores that display label (SP-408 r76/77/78), so we
 * strip the trailing parenthetical here to recover the bare unit name for the
 * results table. Measurement-unit names never contain parentheses, so this is
 * a no-op for units that have no precision.
 */
function stripUnitPrecision(unitName) {
  return (unitName || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/**
 * Transform API response into ResultRow objects
 * Used when displaying risk calculation results
 *
 * @param {Array} apiData - Raw API response array
 * @param {Object} formData - Form selections { cultures, treatmentType, applicationTechnique }
 * @param {Object} enrichedData - Enriched data with labels { cultures, cultureChildren, treatmentType, applicationTechnique, products }
 * @returns {Array} Array of ResultRow objects
 */
export function transformApiResponseToResultRows(
  apiData,
  formData,
  enrichedData
) {
  const { cultures, treatmentType, applicationTechnique } = formData;

  const data = cultures.map((cultureId, index) => {
    const apiResult = apiData[index] || {};
    // Get culture info from enriched data (check both parents and children)
    const cultureInfo = enrichedData.cultures?.find(
      (c) => c.id === cultureId
    ) ||
      enrichedData.cultureChildren?.find((c) => c.id === cultureId) || {
        id: cultureId,
        name: cultureId,
      };

    return {
      id: `${cultureId}-${Date.now()}-${index}`, // Unique ID for row
      culture: {
        id: cultureId,
        // SP-442 row 13 — name in the language selected on the Cultures step.
        name: resolveCultureName(cultureInfo, cultureId),
        // SP-442 row 14 — height for the hover (from the culture's admin form).
        height: cultureInfo.height || null,
      },
      treatmentType: {
        id: treatmentType,
        name: enrichedData.treatmentType?.name || treatmentType,
      },
      applicationTechnique: {
        id: applicationTechnique,
        name: enrichedData.applicationTechnique?.name || applicationTechnique,
        // SP-442 rows 17/18 — abbreviation shown, full name on hover.
        abbreviation: enrichedData.applicationTechnique?.abbreviation || '',
      },
      products: (enrichedData.products || []).map((p) => ({
        id: p.id,
        name: p.name,
        approvalNumber: p.approvalNumber || '',
        dose: p.dose,
        unit: {
          id: p.unit,
          // SP-442 row 28 — bare unit name, without « Culture ou précision ».
          name: stripUnitPrecision(p.unitName),
        },
        activeSubstances: p.activeSubstances || [],
        listedDosages: p.listedDosages || null,
        labelUrl: p.labelUrl || null,
        // SP-442 row 22 — Bio / Prescription agronomique badges.
        isOrganic: p.isOrganic || false,
        hasPrescription: p.hasPrescription || false,
        prescriptionComment: p.prescriptionComment || '',
      })),
      // Nested symbols structure like activeSubstance table
      symbols: {
        health: {
          acute_toxicity_symbol: apiResult.symbols?.health?.acute_toxicity,
          long_term_effect_symbol: apiResult.symbols?.health?.long_term_effect,
        },
        environment: {
          fishes_symbol: apiResult.symbols?.environment?.fishes,
          birds_symbol: apiResult.symbols?.environment?.birds,
          bees_symbol: apiResult.symbols?.environment?.bees,
          persistence_symbol: apiResult.symbols?.environment?.persistence,
          leaching_symbol: apiResult.symbols?.environment?.leaching,
        },
      },
      // Keep original results for IRS/IRE values
      results: {
        health_risk_index: apiResult.health_risk_index,
        environment_risk_index: apiResult.environment_risk_index,
        // SP-442 row 36 — when false, the whole health block stays empty.
        health_risk_visible: apiResult.health_risk_visible !== false,
        // SP-442 row 61 — when false, the whole environment block stays empty.
        environment_risk_visible: apiResult.environment_risk_visible !== false,
        // SP-442 rows 40/65 — expert-assigned IRS/IRE → "††" superscript.
        health_risk_index_is_expert:
          apiResult.health_risk_index_is_expert || false,
        environment_risk_index_is_expert:
          apiResult.environment_risk_index_is_expert || false,
      },
      createdAt: Date.now(),
    };
  });
  return data;
}

/**
 * Enrich the ScenarioModal selections (Add / Duplicate) via the bulk-initialize
 * endpoint — the same source the main flow uses — so modal-created rows get the
 * full hover data (active substances, culture height, listed dosages, Bio /
 * Prescription badges, technique abbreviation). Returns an enrichedData object
 * shaped for transformApiResponseToResultRows.
 *
 * @param {Object} selections - { cultures: [id], treatmentType, applicationTechnique, commercialProducts: [{id, dose, unit}] }
 */
export async function enrichModalSelections(selections) {
  const products = selections.commercialProducts || [];
  const bulkRequest = {};
  if (selections.cultures?.length) bulkRequest.culture_ids = selections.cultures;
  if (selections.treatmentType) bulkRequest.treatment_type_id = selections.treatmentType;
  if (selections.applicationTechnique) {
    bulkRequest.application_technique_id = selections.applicationTechnique;
  }
  if (products.length) {
    bulkRequest.product_ids = products.map((p) => p.id).filter(Boolean);
    const unitIds = products.map((p) => p.unit).filter(Boolean);
    bulkRequest.unit_ids = unitIds.filter((id, i) => unitIds.indexOf(id) === i);
  }

  const { RiskCalculatorService } = await import(
    '../../../services/riskCalculatorService'
  );
  const bulkData = await RiskCalculatorService.bulkInitialize(bulkRequest);

  const cultures = (bulkData.cultures || []).map((c) => ({
    id: c.id,
    value: c.id,
    title: c.french_name || c.english_name || c.latin_name || c.id,
    label: c.french_name || c.english_name || c.latin_name || c.id,
    french_name: c.french_name || '',
    english_name: c.english_name || '',
    latin_name: c.latin_name || '',
    height: c.cultural_height || null,
  }));

  const unitById = {};
  (bulkData.units || []).forEach((u) => {
    unitById[u.id] = u;
  });
  const productById = {};
  (bulkData.products || []).forEach((p) => {
    productById[p.id] = p;
  });
  const enrichedProducts = products.map((p) => {
    const prod = productById[p.id] || { id: p.id, name: p.id };
    const unit = unitById[p.unit] || { id: p.unit, name: p.unit };
    const doses = prod.doses || [];
    return {
      id: prod.id,
      name: prod.name,
      approvalNumber: prod.approval_number || '',
      productTypes: prod.product_types || [],
      dose: p.dose,
      unit: p.unit,
      unitName: unit.name,
      listedDosages: doses.length > 0 ? doses.join(' • ') : null,
      isOrganic: prod.is_organic_approval || false,
      hasPrescription: prod.agronomic_prescription_icon || false,
      prescriptionComment: prod.agronomic_prescription_comment || '',
      activeSubstances: prod.active_substances || [],
    };
  });

  return {
    cultures,
    cultureChildren: [],
    treatmentType: bulkData.treatment_type || null,
    applicationTechnique: bulkData.application_technique || null,
    products: enrichedProducts,
  };
}

/**
 * Format cascading select option with approval number
 * Used in ProductCascadingSelect for option display
 *
 * @param {Object} opt - Raw option from API
 * @param {string} type - Type of option ('product' | 'unit')
 * @returns {Object} Formatted option for Select component
 */
export function formatCascadingOption(opt, type = 'product') {
  if (type === 'product') {
    // Format label as "Product name (approval_number)" if approval_number exists
    const label = opt.approval_number
      ? `${opt.name} (${opt.approval_number})`
      : opt.name;

    return {
      label,
      value: opt.id,
      // Suppress rc-select's native `title` attribute on the option wrapper
      // so it doesn't duplicate the AntD Tooltip we add in productOptionRender.
      title: '',
      count: opt.count,
      doses: opt.doses || [],
      approval_number: opt.approval_number || '',
      name: opt.name,
      badges: opt.badges || [], // rule 55 — Bio / Fin / Nouveau
      rawData: opt,
    };
  }

  // Default format for units, techniques, treatment types, etc.
  // `group` is present for techniques (rule 41/43 — place_category grouping).
  return {
    label: opt.name,
    value: opt.id,
    count: opt.count,
    group: opt.group,
    rawData: opt,
  };
}

/**
 * Build enriched product object for storage
 * Used when saving product data with unit names
 *
 * @param {Object} row - Row data with product info
 * @returns {Object} Enriched product object
 */
export function buildEnrichedProduct(row) {
  // Keep sageDoses as array for re-initialization
  const sageDoses = row.sageDoses || [];
  return {
    id: row.id,
    name: row.name,
    approvalNumber: row.approvalNumber || '',
    dose: row.dose,
    unit: row.unit,
    unitName: row.unitName || '',
    // Store original array for re-initialization
    sageDoses: sageDoses,
    // Formatted string for results table display
    listedDosages: sageDoses.length > 0 ? sageDoses.join(' • ') : null,
  };
}
